<template>
  <span>
    <admin-wrapper
      :straight-title="additionalModuleName"
      :module-enabler="moduleEnabler"
      :inputs="inputs"
      :is-form-fetched="isFetched"
      :has-buttons="false"
    >
      <template #[`devices.input`]>
        <v-col cols="12">
          <v-row no-gutters align="center" class="pt-3">
            <v-col cols="auto" class="pr-4 font-weight-bold">
              {{ $i18n.t('admin.mobileDeviceRegistry.devicesList') }}
            </v-col>
            <v-col cols="auto">
              <base-rounded-button
                icon="mdi-plus"
                size="x-small"
                color="success"
                :tooltip="$i18n.t('admin.mobileDeviceRegistry.addDevice')"
                @click="dialogAction('add')"
              />
            </v-col>
          </v-row>
          <v-row no-gutters class="pt-7">
            <v-col cols="5">
              <template v-if="devices.length > 0">
                <ribbon-list :listItems="devices" item-title-key="name" styleType="rawHigher">
                  <template #itemTitle-prepend=" { item }">
                    <v-tooltip color="font" bottom>
                      <template #activator="{ on }">
                        <v-icon :color="item.paired ? 'success' : 'error'" v-on="on">
                          mdi-circle-medium
                        </v-icon>
                      </template>
                      {{ $i18n.t(`admin.mobileDeviceRegistry.${item.paired ? 'paired' : 'unpaired'}`) }}
                    </v-tooltip>
                  </template>
                  <template #itemButtons-replace="{ item }">
                    <base-button
                      class="mr-1"
                      :translation-path="
                        item.paired ? 'admin.mobileDeviceRegistry.unpair' : 'admin.mobileDeviceRegistry.pair'
                      "
                      @click="dialogAction(item.paired ? 'unpair' : 'pair', item)"
                    >
                    </base-button>
                  </template>
                  <template #itemButtons-append="{ item }">
                    <dots-menu
                      :items="deviceDotsItems"
                      @edit="dialogAction('edit', item)"
                      @delete="dialogAction('delete', item)"
                    />
                  </template>
                </ribbon-list>
              </template>
              <template v-else>
                <italic-subheader translation-path="admin.mobileDeviceRegistry.noDevices" />
              </template>
            </v-col>
          </v-row>
        </v-col>
      </template>
    </admin-wrapper>
    <main-dialog
      v-if="isMainDialogLoaded"
      :is-visible.sync="isMainDialogVisible"
      v-bind="mainDialogBind"
      @agree="dialogActionHandler"
    />
    <main-input-dialog
      v-if="isMainInputDialogLoaded"
      :is-visible.sync="isMainInputDialogVisible"
      v-bind="mainInputDialogBind"
      @agree="dialogActionHandler"
    />
    <main-large-dialog
      v-if="isMainLargeDialogLoaded"
      :is-visible.sync="isMainLargeDialogVisible"
      v-bind="mainLargeDialogBind"
      @agree="dialogActionHandler"
    >
      <template #body>
        <v-col cols="12">
          <v-row no-gutters class="pt-1">
            <v-alert
              dense
              text
              icon="mdi-information-outline"
              color="primary"
              class="text-body-2 text-left mobile-device-pair-alert"
            >
              <i18n path="admin.mobileDeviceRegistry.pairDeviceInfo" tag="span">
                <template #type>
                  <span
                    class="font-italic text-decoration-underline"
                    style="cursor: pointer"
                    @click="openDocumentation"
                  >
                    {{ $i18n.t('admin.mobileDeviceRegistry.documentation') }}
                  </span>
                </template>
              </i18n>
            </v-alert>
          </v-row>
          <v-row no-gutters justify="center" class="mobile-device-pair-name pt-3">
            {{ currentActionData.name }}
          </v-row>
          <v-row no-gutters justify="center" class="pt-5">
            <qrcode-vue :value="currentPairingUrl" size="250" level="H" />
          </v-row>
        </v-col>
      </template>
    </main-large-dialog>
  </span>
</template>

<script>
import { call, get } from 'vuex-pathify';

import AdminWrapper from '@/components/AdminWrapper';
import DotsMenu from '@/components/DotsMenu';
import ItalicSubheader from '@/components/ItalicSubheader';
import QrcodeVue from 'qrcode.vue';
import RibbonList from '@/components/RibbonList';

export default {
  name: 'MobileDeviceRegistrySettings',
  components: {
    AdminWrapper,
    DotsMenu,
    ItalicSubheader,
    QrcodeVue,
    RibbonList,
    MainDialog: () => import('@/components/MainDialog'),
    MainInputDialog: () => import('@/components/MainInputDialog'),
    MainLargeDialog: () => import('@/components/MainLargeDialog'),
  },
  computed: {
    devicesObject: get('admin/mobileRegistryDevices'),
    devices() {
      return this.$_objectToArray(this.devicesObject);
    },
    users: get('users/users'),
    additionalModuleName: get('admin/additionalModules@MOBILE_DEVICES.verbose_name'),
    isModuleEnabled: get('admin/settingsValues@mobile_devices_enabled'),
    moduleEnabler() {
      return {
        name: 'mobile_devices_enabled',
        straightText: this.additionalModuleName,
        subtitle: this.$i18n.t('admin.moduleEnablerSubtitle', { suffix: this.additionalModuleName }),
      };
    },
    inputs() {
      return [
        {
          name: 'devices',
          isNamedSlot: false,
        },
      ];
    },
    capitalizeCurrentAction() {
      return this.$_capitalize(this.currentAction);
    },
    currentDialogActionData() {
      return this.dialogsActionsData[this.currentAction] || {};
    },
    deviceDotsItems() {
      return [{ name: 'edit' }, { name: 'delete' }];
    },
    mainDialogBind() {
      return {
        loading: this.isMainDialogLoading,
        ...(this.isMainDialogVisible
          ? {
              title: this.$i18n.t(`admin.mobileDeviceRegistry.${this.currentAction}DeviceTitle`),
              body: this.$i18n.t(`admin.mobileDeviceRegistry.confirmDevice${this.capitalizeCurrentAction}`, {
                name: this.currentActionData.name,
              }),
              agreeText: this.currentDialogActionData.agreePath,
            }
          : {}),
      };
    },
    mainLargeDialogBind() {
      return {
        loading: this.isMainLargeDialogLoading,
        dialogWidth: '33%',
        ...(this.isMainLargeDialogVisible
          ? {
              title: this.$i18n.t(`admin.mobileDeviceRegistry.${this.currentAction}DeviceTitle`),
              isAgreeVisible: false,
            }
          : {}),
      };
    },
    mainInputDialogBind() {
      return {
        loading: this.isMainInputDialogLoading,
        dialogWidth: '40%',
        ...(this.isMainInputDialogVisible
          ? {
              titleTranslation: this.$i18n.t(`admin.mobileDeviceRegistry.${this.currentAction}DeviceTitle`),
              agreeText: this.currentDialogActionData.agreePath,
              inputs: this.mainInputDialogInputs,
            }
          : {}),
      };
    },
    mainInputDialogInputs() {
      const elementEditInputs = [
        {
          name: 'name',
          translationPath: 'admin.mobileDeviceRegistry.deviceName',
          subtitle: this.$i18n.t('admin.mobileDeviceRegistry.deviceNameSubtitle'),
          cols: '12',
          rules: [v => !!v || v == '0' || 'rules.required'],
          dataType: {
            name: 'text',
          },
        },
        {
          name: 'users',
          translationPath: 'admin.mobileDeviceRegistry.users',
          subtitle: this.$i18n.t('admin.mobileDeviceRegistry.usersSubtitle'),
          cols: '12',
          dataType: {
            name: 'select',
          },
          autoConvertSelect: false,
          multiple: true,
          items: Object.values(this.users),
          itemValue: 'id',
          itemText: 'name',
          hideDetails: true,
        },
      ];
      const mainInputDialogInputsDict = {
        add: elementEditInputs,
        edit: elementEditInputs,
      };
      return (mainInputDialogInputsDict[this.currentAction] || []).map(input => {
        return { ...input, value: JSON.parse(JSON.stringify(this.currentActionData?.[input.name] || '')) };
      });
    },
  },
  data: () => ({
    currentAction: '',
    currentActionData: undefined,
    currentPairingUrl: '',
    dialogsActionsData: {
      add: {
        agreePath: 'dialog.agreeAdd',
        component: 'MainInputDialog',
      },
      delete: {
        agreePath: 'dialog.agreeDelete',
        component: 'MainDialog',
      },
      edit: {
        agreePath: 'dialog.agreeSave',
        component: 'MainInputDialog',
      },
      unpair: {
        agreePath: 'dialog.agreeUnpair',
        component: 'MainDialog',
      },
      pair: {
        agreePath: 'dialog.agreeSave',
        component: 'MainLargeDialog',
        customInitAction: 'handlerGetPairUuid',
      },
    },
    isMainDialogLoaded: false,
    isMainDialogLoading: false,
    isMainDialogVisible: false,
    isMainInputDialogLoaded: false,
    isMainInputDialogVisible: false,
    isMainInputDialogLoading: false,
    isMainLargeDialogLoaded: false,
    isMainLargeDialogVisible: false,
    isMainLargeDialogLoading: false,
    isFetched: false,
    pairingInterval: undefined,
  }),
  methods: {
    addDevice: call('admin/addMobileRegistryDevice'),
    deleteDevice: call('admin/deleteMobileRegistryDevice'),
    editDevice: call('admin/editMobileRegistryDevice'),
    unpairDevice: call('admin/unpairMobileRegistryDevice'),
    getDevices: call('admin/getMobileRegistryDevices'),
    getDevice: call('admin/getMobileRegistryDevice'),
    getPairUuid: call('admin/getPairingMobileRegistyDeviceUuid'),
    getUsers: call('users/getUsers'),
    async init() {
      await Promise.all([this.getUsers(), this.getDevices()]);
      this.isFetched = true;
    },
    dialogAction(type, value = '') {
      this.currentAction = type;
      this.currentActionData = JSON.parse(JSON.stringify(value));
      const { component, customInitAction } = this.dialogsActionsData[type];
      if (customInitAction) {
        this[customInitAction](component);
      } else {
        this[`is${component}Loaded`] = true;
        this[`is${component}Loading`] = false;
        this[`is${component}Visible`] = true;
      }
    },
    async dialogActionHandler() {
      const dialogComponent = this.currentDialogActionData?.component;
      this[`is${dialogComponent}Loading`] = true;
      try {
        await this[`handler${this.capitalizeCurrentAction}`]();
        this[`is${dialogComponent}Visible`] = false;
      } finally {
        this[`is${dialogComponent}Loading`] = false;
      }
    },
    async handlerAdd() {
      const { users: assigned_users, name: device_name } = this.$_arrayToObject(
        this.mainInputDialogInputs,
        'name',
        'value'
      );
      await this.addDevice({ assigned_users, device_name });
    },
    async handlerEdit() {
      const { users: assigned_users, name: device_name } = this.$_arrayToObject(
        this.mainInputDialogInputs,
        'name',
        'value'
      );
      await this.editDevice({ deviceId: this.currentActionData?.id, payload: { assigned_users, device_name } });
    },
    async handlerDelete() {
      await this.deleteDevice(this.currentActionData?.id);
    },
    async handlerUnpair() {
      await this.unpairDevice(this.currentActionData?.id);
    },
    async handlerGetPairUuid(component) {
      const { id, uuid } = await this.getPairUuid(this.currentActionData?.id);
      const { name } = this.currentActionData;
      this.currentPairingUrl = JSON.stringify({
        api: process.env.VUE_APP_API_URL,
        device: {
          deviceId: id,
          deviceName: name,
          pairingUuid: uuid,
        },
      });
      this[`is${component}Loaded`] = true;
      this[`is${component}Loading`] = false;
      this[`is${component}Visible`] = true;
    },
    async refreshPairing() {
      const { id } = this.currentActionData;
      const device = await this.getDevice(id);
      if (device.paired) {
        this.clearParingInterval(true);
      }
    },
    openDocumentation() {
      window.open('https://dokumentacja.aquagis.pl/knowledge-base/aplikacja-mobilna/aquagis-mobile-parowanie-urzadzen');
    },
    clearParingInterval(withSuccessPairing = false) {
      clearInterval(this.pairingInterval);
      this.pairingInterval = undefined;
      if (withSuccessPairing) {
        const { component } = this.currentDialogActionData;
        const { name } = this.currentActionData;
        this.$store.set('snackbar/PUSH_SUCCESSFULLY_MESSAGE!', {
          message: this.$i18n.t('admin.mobileDeviceRegistry.pairedSuccess', { name }),
        });
        this[`is${component}Visible`] = false;
      }
    },
  },
  watch: {
    isModuleEnabled(nV) {
      if (nV) {
        this.isFetched = false;
        this.init();
      }
    },
    currentPairingUrl(nV, oV) {
      if (nV && nV !== oV) {
        this.pairingInterval = setInterval(() => {
          this.refreshPairing();
        }, 5000);
      } else if (!nV) {
        this.clearParingInterval();
      }
    },
  },
  mounted() {
    this.$watch(
      vm => [vm.isMainDialogVisible, vm.isMainInputDialogVisible, vm.isMainLargeDialogVisible],
      nV => {
        const [isMainDialogVisible, isMainInputDialogVisible, isMainLargeDialogVisible] = nV;
        if (!isMainDialogVisible && !isMainInputDialogVisible && !isMainLargeDialogVisible) {
          this.currentAction = '';
          this.currentPairingUrl = '';
          this.isMainDialogLoading = false;
          this.isMainInputDialogLoading = false;
          this.isMainLargeDialogLoading = false;
          this.clearParingInterval();
        }
      }
    );
  },
  created() {
    if (this.isModuleEnabled) {
      this.init();
    } else {
      this.isFetched = true;
    }
  },
  beforeDestroy() {
    this.clearParingInterval();
  },
};
</script>
<style lang="scss" scoped>
.mobile-device-pair-alert {
  ::v-deep {
    .v-alert__wrapper {
      .v-alert__icon {
        align-self: center;
      }
    }
  }
}
.mobile-device-pair-name {
  font-weight: 500;
  font-size: 16px;
  color: #202124;
}
</style>
